import { useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Col, Row } from 'reactstrap';
import { isMobile } from 'react-device-detect';
import { toast } from 'react-toastify';
import useModal from '../useModal/useModal';
import useFiltersWidget from './useFiltersWidget';

const FILTER_LABELS = {
  name: 'Filter Set Name',
  categories: 'Category',
  segments: 'Customer Segment',
  locations: 'Parent Location',
  children: 'Child Location',
  distributors: 'Distributor',
  products: 'Product',
  countries: 'Country',
  states: 'State/Territory',
  chainData: 'Chain Data',
  emergingData: 'Emerging Chain Data',
  startDate: 'Start Date',
  endDate: 'End Date'
};

const FILTER_ORDER = [
  'name',
  'categories',
  'segments',
  'locations',
  'children',
  'distributors',
  'products',
  'countries',
  'states',
  'chainData',
  'emergingData',
  'startDate',
  'endDate'
];

const DATASET_FILTER_KEYS = ['chainData', 'emergingData'];

const normalizeValue = (value) => {
  if (Array.isArray(value)) {
    return [...value].sort((left, right) => `${left}`.localeCompare(`${right}`));
  }

  if (typeof value === 'boolean') {
    return value;
  }

  return value ?? null;
};

const formatValue = (value) => {
  if (typeof value === 'boolean') {
    return value ? 'Included' : null;
  }

  return value || 'None';
};

const getValueChanges = (currentValue, nextValue) => {
  if (Array.isArray(currentValue) || Array.isArray(nextValue)) {
    const currentItems = Array.isArray(currentValue) ? currentValue : [];
    const nextItems = Array.isArray(nextValue) ? nextValue : [];
    const removedItems = currentItems.filter((item) => !nextItems.includes(item));
    const addedItems = nextItems.filter((item) => !currentItems.includes(item));

    return [
      ...removedItems.map((item) => ({ type: 'removed', value: item })),
      ...addedItems.map((item) => ({ type: 'added', value: item }))
    ];
  }

  const changes = [];

  const formattedCurrentValue = formatValue(currentValue);
  const formattedNextValue = formatValue(nextValue);

  if (formattedCurrentValue !== null && formattedCurrentValue !== undefined) {
    changes.push({ type: 'removed', value: formattedCurrentValue });
  }

  if (formattedNextValue !== null && formattedNextValue !== undefined) {
    changes.push({ type: 'added', value: formattedNextValue });
  }

  return changes;
};

const getFilterDifferences = (currentFilters = {}, nextFilters = {}, currentFilterSetName = null, nextFilterSetName = null) => {
  const nameChanges = getValueChanges(currentFilterSetName, nextFilterSetName);
  const differences = [];

  if (nameChanges.length > 0) {
    differences.push({
      key: 'name',
      label: FILTER_LABELS.name,
      changes: nameChanges
    });
  }

  return FILTER_ORDER.reduce((diffs, key) => {
    if (key === 'name') {
      return diffs;
    }

    const currentValue = normalizeValue(currentFilters[key]);
    const nextValue = normalizeValue(nextFilters[key]);

    if (DATASET_FILTER_KEYS.includes(key) && nextValue !== true) {
      return diffs;
    }

    if (JSON.stringify(currentValue) === JSON.stringify(nextValue)) {
      return diffs;
    }

    const changes = getValueChanges(currentValue, nextValue);

    if (changes.length === 0) {
      return diffs;
    }

    diffs.push({
      key,
      label: FILTER_LABELS[key] || key,
      changes
    });

    return diffs;
  }, differences);
};

const FilterSetUpdateModal = ({ id, ...props }) => {
  const { modalProps, headerProps, toggle } = useModal(id, { hideClose: false, header: { color: 'black', background: 'white' } });
  const { updateFilterSet } = useFiltersWidget();
  const { filterSet, existingFilters, selectedFilters, existingFilterSetName, onClose } = props;
  const [isSaving, setIsSaving] = useState(false);
  const differences = useMemo(
    () => getFilterDifferences(existingFilters, selectedFilters, existingFilterSetName, filterSet?.label),
    [existingFilters, selectedFilters, existingFilterSetName, filterSet?.label]
  );

  const handleModalClosed = () => {
    modalProps.onClosed?.();
    onClose?.();
  };

  const onConfirm = () => {
    if (!filterSet?.value || differences.length === 0 || isSaving) {
      return;
    }

    setIsSaving(true);

    updateFilterSet(filterSet.value, filterSet.label, selectedFilters)
      .then(() => {
        toggle();
        toast.success('Filter Set Updated!');
      })
      .catch(() => {
        toast.error('Unable to update filter set. Please try again.');
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <Modal {...modalProps} onClosed={handleModalClosed} modalClassName="filters-set-update-modal">
      <ModalHeader {...headerProps}>Update Filter Set</ModalHeader>
      <ModalBody className={isMobile ? 'px-2' : 'pt-0'}>
        <Row>
          <Col>
            <div style={{ color: 'black', fontWeight: '500' }} className="main-header pt-2">
              Review the changes to {filterSet?.label || 'this filter set'} before saving them.
            </div>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            {differences.length === 0 && (
              <div style={{ color: 'black' }}>No filter changes were detected.</div>
            )}
            {differences.length > 0 && (
              <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 8, backgroundColor: '#fbfcfe' }}>
                {differences.map((difference, index) => (
                  <div
                    key={difference.key}
                    style={{
                      padding: '14px 16px',
                      borderBottom: index === differences.length - 1 ? 'none' : '1px solid #ececec'
                    }}
                  >
                    <Row
                      style={{
                        gap: 10,
                        alignItems: 'flex-start'
                      }}
                    >
                      <Col xs="12">
                        <div style={{ color: '#0b3558', fontWeight: 700 }}>{difference.label}</div>
                        <div style={{ color: '#7a8695', fontSize: 12, marginTop: 4 }}>
                          {difference.changes.length > 1 ? 'Changed options' : 'Changed value'}
                        </div>
                      </Col>
                      <Col xs="12">
                        <Row
                          style={{
                            gap: 8,
                            width: '100%',
                            justifySelf: 'center'
                          }}
                        >
                          {difference.changes.map((change) => {
                            const isAdded = change.type === 'added';

                            return (
                              <Col xs="12"
                                key={`${difference.key}-${change.type}-${change.value}`}
                                style={{
                                  border: `1px solid ${isAdded ? '#c7e5ce' : '#f0c8c8'}`,
                                  borderRadius: 6,
                                  backgroundColor: isAdded ? '#f3fff5' : '#fff5f5',
                                  padding: '10px 12px',
                                  width: '100%'
                                }}
                              >
                                <Row style={{ gap: 10, alignItems: 'flex-start' }}>
                                <Col xs="auto"
                                  style={{
                                    color: isAdded ? '#2f7d40' : '#9b3d3d',
                                    fontFamily: 'Consolas, Monaco, monospace',
                                    fontSize: 16,
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    minWidth: 12,
                                    paddingRight: 0
                                  }}
                                >
                                  {isAdded ? '+' : '-'}
                                </Col>
                                <Col style={{ color: '#222', fontFamily: 'Consolas, Monaco, monospace', fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word', paddingLeft: 0 }}>
                                  {change.value}
                                </Col>
                                </Row>
                              </Col>
                            );
                          })}
                        </Row>
                      </Col>
                    </Row>
                  </div>
                ))}
              </div>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter className="justify-content-center">
        <Col className="pr-0">
          <Button
            disabled={differences.length === 0 || isSaving}
            onClick={onConfirm}
            block
            className="py-2"
            style={{ float: 'right', color: 'white' }}
            color="blueFilter"
          >
            Update Filter Set
          </Button>
        </Col>
      </ModalFooter>
    </Modal>
  );
};

export default FilterSetUpdateModal;